// About — who we are, how the courses run, and the questions we get asked most.
// Anything factual (prices, hours, ages) is read from courseData so this page
// can't drift out of sync with the courses page.
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { COURSES } from '../components/courses/courseData';
import { injectFonts, T } from '../utils/fonts';

const PHONE = '054-944-7314';
const EMAIL = 'gogamedev3d@gmail.com';
const WHATSAPP = 'https://wa.me/972549447314';

const PILLARS = [
  { icon: '🎮', title: 'פיתוח משחקים', desc: 'לומדים לבנות משחקים תלת-ממדיים ודו-ממדיים מהאפס עם מנוע גודו.', accent: T.godotBlue },
  { icon: '⛏️', title: 'תכנות במיינקראפט', desc: 'לומדים לתכנת דרך העולם שהם כבר אוהבים — מבלוקים ועד קוד Python אמיתי.', accent: T.minecraft },
  { icon: '🐍', title: 'תכנות פייתון', desc: 'יסודות התכנות בשפה הפופולרית ביותר — חשיבה אלגוריתמית בצורה מהנה.', accent: T.python },
  { icon: '🌐', title: 'אתרים תלת-ממדיים', desc: 'בניית חוויות אינטרנט עם גרפיקה תלת-ממדית אינטראקטיבית בזמן אמת.', accent: T.web3d },
  { icon: '🧑‍🏫', title: 'למידה אישית', desc: 'שיעורים קטנים ומותאמים אישית — כל תלמיד מתקדם בקצב שלו.', accent: T.green },
  { icon: '🏆', title: 'פרויקטים אמיתיים', desc: 'בסיום כל קורס התלמידים יוצרים פרויקט עצמאי מלא שניתן להציג.', accent: T.gold },
  { icon: '🧒', title: 'מותאם לגיל', desc: 'קבוצות נפרדות לגילאי 10–13 ו-14–18 — תוכן מותאם, הסברים ברורים, אפס שעמום.', accent: T.godot },
];

const FAQ = [
  {
    q: 'לאיזה גילאים הקורסים מתאימים?',
    a: (
      <p>
        הקורסים מיועדים לגילאי 10–18, בקבוצות נפרדות: 10–13 ו-14–18. החלוקה מאפשרת
        להתאים את הקצב, הדוגמאות ואופי ההסבר לכל קבוצת גיל.
      </p>
    ),
  },
  {
    q: 'צריך ידע קודם בתכנות?',
    a: (
      <p>
        לא. כל הקורסים מתחילים מהבסיס ובונים את הידע שלב אחר שלב, כך שאפשר להצטרף
        גם בלי שום רקע קודם.
      </p>
    ),
  },
  {
    q: 'איפה מתקיימים השיעורים?',
    a: (
      <p>
        השיעורים מתקיימים אונליין, בכיתות קטנות עם ליווי אישי — כך שאפשר להשתתף
        מכל מקום, בלי נסיעות.
      </p>
    ),
  },
  {
    q: 'כמה זמן נמשך כל קורס וכמה שעות הוא כולל?',
    a: (
      <ul>
        {COURSES.map((c) => (
          <li key={c.id}>
            <strong>{c.title}</strong> — {c.totalHours}. {c.schedule}.
          </li>
        ))}
      </ul>
    ),
  },
  {
    q: 'מה העלות?',
    a: (
      <>
        <ul>
          {COURSES.map((c) => (
            <li key={c.id}>
              <strong>{c.title}</strong> — ₪{c.price} לחודש.
            </li>
          ))}
        </ul>
        <p>
          המחירים הם לחודש לימודים. רוב הכלים שאנחנו מלמדים — גודו, פייתון וכלי
          פיתוח האתרים — חינמיים לחלוטין. קורס מיינקראפט דורש רישיון
          Minecraft Education Edition, ונשמח להסביר על כך בהרשמה.
        </p>
      </>
    ),
  },
  {
    q: 'מה נדרש מבחינת ציוד ותוכנה?',
    a: (
      <p>
        מחשב עם חיבור לאינטרנט, ורצוי אוזניות עם מיקרופון. גודו, פייתון וכלי פיתוח
        האתרים חינמיים לחלוטין, ונעזור להתקין אותם בשיעור הראשון. לקורס מיינקראפט
        נדרש בנוסף Minecraft Education Edition.
      </p>
    ),
  },
  {
    q: 'מהי שעת התגבור?',
    a: (
      <p>
        בנוסף לשיעור השבועי יש שעת תגבור — זמן פתוח להשלמות, שאלות ועבודה על
        הפרויקט האישי עם ליווי, לתלמידים שרוצים להעמיק או להשלים חומר.
      </p>
    ),
  },
  {
    q: 'איך נרשמים?',
    a: (
      <p>
        אפשר להשאיר פרטים ב<Link className="gg-link" to="/contact">דף יצירת הקשר</Link>,
        לשלוח הודעה ב<a className="gg-link" href={WHATSAPP} target="_blank" rel="noopener noreferrer">וואטסאפ</a>,
        להתקשר ל<a className="gg-link" href={`tel:${PHONE}`}>{PHONE}</a> או לכתוב
        ל<a className="gg-link" href={`mailto:${EMAIL}`}>{EMAIL}</a>. נחזור אליכם עם כל הפרטים.
      </p>
    ),
  },
];

const About = () => {
  useEffect(() => { injectFonts(); }, []);

  return (
    <div className="about gg-bg" dir="rtl">
      <style>{css}</style>

      <header className="about-hero">
        <span className="gg-node gg-reveal" style={{ '--d': '0.05s' }}>אודות GoGameDev3D</span>
        <h1 className="about-title gg-reveal" style={{ '--d': '0.12s' }}>
          מלמדים את מפתחי<br />המשחקים של המחר
        </h1>
        <p className="about-lead gg-reveal" style={{ '--d': '0.2s' }}>
          אנחנו פלטפורמת למידה אינטראקטיבית המיועדת לילדים ובני נוער שרוצים ללמוד
          פיתוח משחקים, תכנות ואתרים תלת-ממדיים בצורה מהנה ומעשית.
        </p>

        <div className="about-stats gg-reveal" style={{ '--d': '0.28s' }}>
          {[
            [`${COURSES.length}`, 'קורסים'],
            ['10–18', 'גילאים'],
          ].map(([v, l]) => (
            <div className="about-stat" key={l}>
              <span className="about-stat-v">{v}</span>
              <span className="about-stat-l">{l}</span>
            </div>
          ))}
        </div>
      </header>

      <section className="about-section" aria-labelledby="pillars-title">
        <h2 className="about-h2" id="pillars-title">מה לומדים אצלנו</h2>
        <div className="about-grid">
          {PILLARS.map((p, i) => (
            <article
              className="about-card gg-reveal"
              key={p.title}
              style={{ '--accent': p.accent, '--d': `${0.06 * i}s` }}
            >
              <span className="about-card-icon" aria-hidden="true">{p.icon}</span>
              <h3 className="about-card-title">{p.title}</h3>
              <p className="about-card-desc">{p.desc}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="about-section" aria-labelledby="faq-title">
        <h2 className="about-h2" id="faq-title">שאלות נפוצות</h2>
        <div className="about-faq">
          {FAQ.map(({ q, a }) => (
            // <details> gives keyboard support, screen-reader semantics and
            // find-in-page for free — no JS state to get wrong.
            <details className="about-qa" key={q}>
              <summary>
                <span>{q}</span>
                <span className="about-qa-mark" aria-hidden="true" />
              </summary>
              <div className="about-qa-body">{a}</div>
            </details>
          ))}
        </div>
      </section>

      <section className="about-cta">
        <h2 className="about-h2">מוכנים להתחיל?</h2>
        <p className="about-lead">
          בחרו את הקורס שמתאים לכם, או קבעו שיעור ניסיון חינם ונעזור לכם להחליט.
        </p>
        <div className="about-cta-row">
          <Link className="gg-btn" to="/courses">לצפייה בקורסים ←</Link>
          <Link
            className="gg-btn-ghost"
            to="/contact"
            aria-label="שיעור ניסיון חינם — מעבר לדף יצירת קשר"
          >
            שיעור ניסיון חינם
          </Link>
        </div>
      </section>
    </div>
  );
};

const css = `
  .about {
    min-height: 100vh;
    padding: clamp(88px, 14vw, 132px) clamp(18px, 5vw, 40px) clamp(48px, 8vw, 88px);
    font-family: ${T.fontBody};
    color: ${T.text};
  }

  .about-hero { max-width: 780px; margin: 0 auto clamp(44px, 8vw, 84px); text-align: center; }
  .about-hero .gg-node { margin-bottom: 16px; }

  .about-title {
    font-family: ${T.fontDisplay};
    font-size: clamp(2rem, 6.5vw, 3.6rem);
    line-height: 1.15;
    letter-spacing: -0.02em;
    margin: 0 0 clamp(14px, 2.5vw, 20px);
    color: ${T.text};
  }
  .about-lead {
    font-size: clamp(1rem, 2.6vw, 1.15rem);
    line-height: 1.8;
    color: ${T.muted};
    margin: 0 auto;
    max-inline-size: 58ch;
  }

  .about-stats {
    display: flex; justify-content: center;
    gap: clamp(24px, 5vw, 52px);
    margin-top: clamp(26px, 4vw, 38px);
  }
  .about-stat { display: flex; flex-direction: column; }
  .about-stat-v {
    font-family: ${T.fontDisplay};
    font-size: clamp(1.5rem, 3.4vw, 2.1rem);
    line-height: 1; color: ${T.text};
  }
  .about-stat-l {
    margin-top: 7px; font-size: 0.76rem; font-weight: 700;
    letter-spacing: 0.06em; color: ${T.faint};
  }

  .about-section { max-width: 1080px; margin: 0 auto clamp(44px, 8vw, 84px); }
  .about-h2 {
    font-family: ${T.fontDisplay};
    font-size: clamp(1.35rem, 4vw, 2rem);
    margin: 0 0 clamp(18px, 3vw, 28px);
    color: ${T.text};
  }

  .about-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 268px), 1fr));
    gap: clamp(12px, 2.4vw, 20px);
  }
  .about-card {
    position: relative;
    background: ${T.panel};
    border: 1px solid ${T.line};
    border-radius: 16px;
    padding: clamp(18px, 3vw, 26px);
    overflow: hidden;
  }
  .about-card::before {
    content: ''; position: absolute; inset: 0 0 auto 0; height: 2px;
    background: linear-gradient(90deg, transparent, var(--accent), transparent);
    opacity: 0.85;
  }
  .about-card-icon { font-size: 1.7rem; line-height: 1; }
  .about-card-title {
    font-family: ${T.fontDisplay};
    font-size: 1.05rem; margin: 12px 0 8px; color: ${T.text};
  }
  .about-card-desc { margin: 0; color: ${T.muted}; font-size: 0.94rem; line-height: 1.7; }

  /* ── FAQ ── */
  .about-faq { display: flex; flex-direction: column; gap: 10px; }
  .about-qa {
    background: ${T.panel};
    border: 1px solid ${T.line};
    border-radius: 14px;
    overflow: hidden;
  }
  .about-qa summary {
    display: flex; align-items: center; justify-content: space-between; gap: 14px;
    padding: clamp(14px, 2.4vw, 18px) clamp(16px, 2.6vw, 22px);
    cursor: pointer;
    font-weight: 700;
    font-size: clamp(0.95rem, 2.6vw, 1.05rem);
    color: ${T.text};
    list-style: none;
    min-height: 44px;
    transition: background .2s ease;
  }
  .about-qa summary::-webkit-details-marker { display: none; }
  .about-qa summary:hover { background: ${T.panelUp}; }
  .about-qa summary:focus-visible { outline: 2px solid ${T.gold}; outline-offset: -2px; }

  /* Plus sign that becomes a minus when open — drawn, so it needs no icon font */
  .about-qa-mark {
    position: relative; flex-shrink: 0;
    width: 16px; height: 16px;
  }
  .about-qa-mark::before, .about-qa-mark::after {
    content: ''; position: absolute; inset: 50% 0 auto 0;
    height: 2px; border-radius: 2px; background: ${T.godot};
    transition: transform .25s ease;
  }
  .about-qa-mark::after { transform: rotate(90deg); }
  .about-qa[open] .about-qa-mark::after { transform: rotate(0deg); }

  .about-qa-body {
    padding: 0 clamp(16px, 2.6vw, 22px) clamp(16px, 2.6vw, 20px);
    color: ${T.muted};
    line-height: 1.8;
  }
  .about-qa-body p { margin: 0 0 10px; }
  .about-qa-body p:last-child { margin-bottom: 0; }
  .about-qa-body ul { margin: 0; padding-inline-start: 20px; }
  .about-qa-body li { margin-bottom: 6px; }
  .about-qa-body strong { color: ${T.text}; }

  .about-cta { max-width: 680px; margin: 0 auto; text-align: center; }
  .about-cta-row {
    display: flex; gap: 12px; flex-wrap: wrap; justify-content: center;
    margin-top: clamp(20px, 3vw, 28px);
  }

  @media (max-width: 640px) {
    .about-stats { gap: 22px; }
    .about-cta-row .gg-btn, .about-cta-row .gg-btn-ghost { flex: 1 1 auto; padding-inline: 14px; }
  }
`;

export default About;
